import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Box, Button, Text } from "@salestrak/ui";
import { useActivityContext } from "./activity-context";
import { NoteItem } from "./note-item";
import { NoteComposer } from "./note-composer";
import type { ActivityAuthor } from "./activity-types";
import { useIssueViewCapabilities } from "../issue-view-capabilities";
import type { Omittable } from "@salestrak/ui";

type NoteThreadProps = Omittable<{
  issueId: string;
  noteId: string;
  headerExtra?: React.ReactNode;
}>;

export function NoteThread({ issueId, noteId, headerExtra }: NoteThreadProps) {
  const { getNote, selectedParentId } = useActivityContext("NoteThread");
  const note = getNote(noteId);
  const [collapsed, setCollapsed] = useState(true);
  // View-only viewers can't post replies, so the composer slot
  // collapses to nothing and we drop its wrapping padding too.
  const { canComment } = useIssueViewCapabilities();

  useEffect(() => {
    if (selectedParentId === noteId && collapsed) {
      setCollapsed(false);
    }
  }, [selectedParentId, noteId, collapsed]);

  const replyInfo = useMemo(() => {
    if (!collapsed || !note) return null;

    const replies = note.replies ?? [];
    const uniqueAuthors = new Map<string, ActivityAuthor>();

    for (const reply of replies) {
      if (reply.createdBy && reply.author && !uniqueAuthors.has(reply.createdBy)) {
        uniqueAuthors.set(reply.createdBy, reply.author);
      }
    }

    return {
      count: note.replyCount ?? replies.length,
      authors: Array.from(uniqueAuthors.values()),
    };
  }, [collapsed, note]);

  const handleExpandThread = useCallback(() => {
    setCollapsed(false);
  }, []);

  const handleReplyRequest = useCallback(() => {
    setCollapsed(false);
  }, []);

  if (!note) return null;

  const replies = note.replies ?? [];

  return (
    <Box marginBottom="md">
      <NoteItem
        issueId={issueId}
        noteId={noteId}
        isParent
        headerExtra={headerExtra}
        onRequestReply={handleReplyRequest}
        onExpandThread={handleExpandThread}
        replyInfo={replyInfo}
        showFooterBorder={collapsed}
      />

      {!collapsed && (
        <Box
          border={["bottom", "left", "right"]}
          // A continuation of the parent note card, not a new card — fill
          // only, no second shadow.
          background="card"
          overflow="hidden"
          className="rounded-b-lg"
        >
          {replies.length > 0 && (
            <Box
              direction="column"
              gap="xs"
              paddingX="md"
              paddingTop="md"
              paddingBottom="sm"
            >
              {replies.map((reply) => (
                <NoteItem
                  key={reply.noteId}
                  issueId={issueId}
                  noteId={reply.noteId}
                  onRequestReply={handleReplyRequest}
                />
              ))}
            </Box>
          )}

          {canComment && (
            <Box paddingX="md" paddingY="md">
              <NoteComposer issueId={issueId} parentNoteId={noteId} />
            </Box>
          )}

          <Box
            align="center"
            justify="center"
            paddingY="sm"
            paddingX="md"
            border="top"
            background="card"
          >
            <Button variant="link" size="sm" onClick={() => setCollapsed(true)}>
              <Text size="xs" variant="muted">
                Hide
              </Text>
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
